import React, { FC, useEffect } from 'react';
import { Box, Grid, Paper, TextField, Typography } from '@mui/material';
import { LabClient } from 'models/client-directory';
import { useTranslation } from 'react-i18next';
import AddressForm from 'components/shared/address/address-form.component';
import { useFormik } from 'formik';
import { OverrideContactFormData } from 'models/forms';
import { LoadingButton } from '@mui/lab';
import { isValidEmail } from 'utils/validations';
import { Address } from 'models/contact';

type OverridenContactFormProps = {
    labClient: LabClient;
    onContactChanged?: (formData: OverrideContactFormData) => void;
    loading: boolean;
};

const OverridenContactForm: FC<OverridenContactFormProps> = ({
    labClient,
    onContactChanged,
    loading,
}: OverridenContactFormProps) => {
    const { t: translate } = useTranslation(['common']);
    const [isLoading, setIsLoading] = React.useState(false);

    const validate = (formData: OverrideContactFormData) => {
        const errors: Record<string, string> = {};
        if (formData.email && !isValidEmail(formData.email)) {
            errors.email = translate('organization:VALIDATION:INVALID_EMAIL');
        }
        if (!formData.address) {
            errors.address = 'ADDRESS_IS_REQUIRED';
        }
        return errors;
    };

    const formik = useFormik<OverrideContactFormData>({
        initialValues: {
            email: '',
            phone: '',
            site: '',
            address: undefined,
        },
        validate,
        onSubmit: async (formData) => {
            if (onContactChanged) {
                onContactChanged(formData);
            }
        },
    });

    useEffect(() => {
        formik.setValues({
            email: labClient?.clientContent?.overriddenContact?.communication?.email || '',
            phone: labClient?.clientContent?.overriddenContact?.communication?.phone || '',
            site: labClient?.clientContent?.overriddenContact?.communication?.site || '',
            address: labClient?.clientContent?.overriddenContact?.address || undefined,
        });
    }, [labClient]);

    useEffect(() => {
        setIsLoading(loading);
    }, [loading]);

    const handleAddressChange = (address: Address | undefined) => {
        formik.setFieldValue('address', address);
    };

    return (
        <Paper>
            <form onSubmit={formik.handleSubmit}>
                <Box p={2}>
                    <Typography mb={2} variant="h4">
                        {translate('client-directory:OVERRIDDEN_CONTACT_INFO')}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <AddressForm address={formik.values.address} onChange={handleAddressChange} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField label={translate('common:PHONE')} fullWidth {...formik.getFieldProps('phone')} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label={translate('common:EMAIL')}
                                fullWidth
                                helperText={formik.touched.email && formik.errors.email}
                                error={formik.touched.email && Boolean(formik.errors.email)}
                                {...formik.getFieldProps('email')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label={translate('common:WEBSITE')}
                                fullWidth
                                {...formik.getFieldProps('site')}
                            />
                        </Grid>
                    </Grid>
                    <Box mt={2}>
                        <LoadingButton disabled={!formik.isValid} variant="contained" type="submit" loading={isLoading}>
                            {translate('common:SAVE')}
                        </LoadingButton>
                    </Box>
                </Box>
            </form>
        </Paper>
    );
};

export default OverridenContactForm;
